import React, { useContext, useEffect, useMemo, useState } from "react";
import "../css/shop.scss";
import { Link, useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";

const MIN_PRICE = 35;
const MAX_PRICE = 200;

function Shop() {
  const { wishlist, toggleWishlist } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(searchParams.get("search") || "");
  const [loadedImages, setLoadedImages] = useState({});
  const [failedImages, setFailedImages] = useState({});

  const department = searchParams.get("department") || "All";
  const category = searchParams.get("category") || "All";
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "featured";
  const requestedMaxPrice = Number(searchParams.get("maxPrice") || MAX_PRICE);
  const maxPrice = Number.isFinite(requestedMaxPrice)
    ? Math.min(MAX_PRICE, Math.max(MIN_PRICE, requestedMaxPrice))
    : MAX_PRICE;
  const inStockOnly = searchParams.get("stock") === "1";
  const wishlistOnly = searchParams.get("wishlist") === "1";

  useEffect(() => {
    setSearchDraft(search);
    document.title = `${search ? `Search: ${search}` : "Shop"} — ${siteConfig.brandName}`;
  }, [search]);

  const categories = useMemo(
    () => ["All", ...new Set(productData.map((product) => product.category))],
    []
  );

  const products = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const searchable = [product.name, product.category, product.department, ...product.tags]
        .join(" ")
        .toLowerCase();
      return (
        (department === "All" || product.department === department) &&
        (category === "All" || product.category === category) &&
        (!query || searchable.includes(query)) &&
        product.price <= maxPrice &&
        (!inStockOnly || product.stock > 0) &&
        (!wishlistOnly || wishlist.includes(product.id))
      );
    });

    return [...filtered].sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "name") return a.name.localeCompare(b.name);
      return Number(b.featured) - Number(a.featured) || a.id - b.id;
    });
  }, [category, department, inStockOnly, maxPrice, search, sort, wishlist, wishlistOnly]);

  const setParam = (key, value, defaultValue = "") => {
    const next = new URLSearchParams(searchParams);
    if (value === defaultValue || value === "") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setParam("search", searchDraft.trim());
  };

  const markImage = (id, type) => {
    if (type === "loaded") setLoadedImages((current) => ({ ...current, [id]: true }));
    if (type === "failed") setFailedImages((current) => ({ ...current, [id]: true }));
  };

  const heading = wishlistOnly
    ? "Wishlist"
    : search
      ? `Search results for “${search}”`
      : department !== "All"
        ? `${department} collection`
        : category !== "All"
          ? category
          : "All collections";

  return (
    <main className="shop-page">
      <h1 className="shop-page__title">{heading}</h1>

      {search && (
        <form className="shop-search" onSubmit={submitSearch}>
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            aria-label="Search products"
            type="search"
          />
          <button type="submit">Search</button>
        </form>
      )}

      <div className="shop-toolbar">
        <p>{products.length} products</p>
        <div>
          <button type="button" onClick={() => setFilterOpen(true)}>
            Filter{department !== "All" || category !== "All" || maxPrice < MAX_PRICE || inStockOnly ? " ·" : ""}
          </button>
          <label>
            <span>Sort by</span>
            <select value={sort} onChange={(event) => setParam("sort", event.target.value, "featured")}>
              <option value="featured">Featured</option>
              <option value="price-asc">Price, low to high</option>
              <option value="price-desc">Price, high to low</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>
      </div>

      {products.length > 0 ? (
        <section className="product-grid" aria-live="polite">
          {products.map((product) => {
            const wished = wishlist.includes(product.id);
            return (
              <article className="product-card" key={product.id}>
                <Link to={`/shopDetails/${product.id}`} className="product-card__image" aria-label={product.name}>
                  {!loadedImages[product.id] && !failedImages[product.id] && <span className="product-card__skeleton" />}
                  {failedImages[product.id] ? (
                    <span className="product-card__error">Image unavailable</span>
                  ) : (
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      className={loadedImages[product.id] ? "is-loaded" : ""}
                      onLoad={() => markImage(product.id, "loaded")}
                      onError={() => markImage(product.id, "failed")}
                    />
                  )}
                </Link>
                <button
                  type="button"
                  className={`product-card__wishlist ${wished ? "is-active" : ""}`}
                  onClick={() => toggleWishlist(product.id)}
                  aria-label={wished ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
                >
                  {wished ? "♥" : "♡"}
                </button>
                <Link to={`/shopDetails/${product.id}`} className="product-card__details">
                  <h2>{product.name}</h2>
                  <p>{formatPrice(product.price)}</p>
                </Link>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="shop-empty">
          <p>No pieces match this edit.</p>
          <button type="button" onClick={() => setSearchParams({})}>Clear all filters</button>
        </section>
      )}

      <aside className={`filter-panel ${filterOpen ? "filter-panel--open" : ""}`} aria-hidden={!filterOpen}>
        <div className="filter-panel__head">
          <h2>Filter</h2>
          <button type="button" onClick={() => setFilterOpen(false)}>Close</button>
        </div>

        <fieldset>
          <legend>Collection</legend>
          {["All", "Woman", "Man"].map((value) => (
            <label key={value}>
              <input
                type="radio"
                name="department"
                checked={department === value}
                onChange={() => setParam("department", value, "All")}
              />
              <span>{value}</span>
            </label>
          ))}
        </fieldset>

        <fieldset>
          <legend>Category</legend>
          {categories.map((value) => (
            <label key={value}>
              <input
                type="radio"
                name="category"
                checked={category === value}
                onChange={() => setParam("category", value, "All")}
              />
              <span>{value}</span>
            </label>
          ))}
        </fieldset>

        <fieldset>
          <legend>Price up to {formatPrice(maxPrice)}</legend>
          <input
            className="filter-panel__range"
            type="range"
            min={MIN_PRICE}
            max={MAX_PRICE}
            step="5"
            value={maxPrice}
            onChange={(event) => setParam("maxPrice", event.target.value, String(MAX_PRICE))}
          />
        </fieldset>

        <fieldset>
          <legend>Availability</legend>
          <label>
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={(event) => setParam("stock", event.target.checked ? "1" : "")}
            />
            <span>In stock only</span>
          </label>
        </fieldset>

        <div className="filter-panel__actions">
          <button type="button" onClick={() => setSearchParams(wishlistOnly ? { wishlist: "1" } : {})}>Clear</button>
          <button type="button" onClick={() => setFilterOpen(false)}>Show {products.length} products</button>
        </div>
      </aside>
      {filterOpen && <button className="shop-overlay" type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" />}
    </main>
  );
}

export default Shop;
